import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import AdminLayout from './AdminLayout';

export default function TestCentres() {
  const navigate = useNavigate();
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ code: '', name: '', city: '', postcode: '', region: '' });
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ code: '', name: '', city: '', postcode: '', region: '' });

  const fetchCentres = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get('/test-centres?all=true');
      setCentres(Array.isArray(data) ? data : (data?.testCentres || data?.centres || []));
    } catch (err) {
      setError(err.message || 'Failed to load test centres');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCentres(); }, [fetchCentres]);

  const toggle = async (id, isActive) => {
    try {
      await api.patch(`/test-centres/${id}`, { is_active: !isActive });
      setCentres(prev => prev.map(c => c.id === id ? { ...c, is_active: !isActive } : c));
    } catch (err) {
      alert(err.message || 'Failed to update');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const created = await api.post('/test-centres', form);
      setCentres(prev => [...prev, created?.testCentre || created]);
      setForm({ code: '', name: '', city: '', postcode: '', region: '' });
      setShowAdd(false);
    } catch (err) {
      alert(err.message || 'Failed to add test centre');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (c) => {
    setEditItem(c);
    setEditForm({ code: c.code || '', name: c.name || '', city: c.city || '', postcode: c.postcode || '', region: c.region || '' });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/test-centres/${editItem.id}`, editForm);
      setCentres(prev => prev.map(c => c.id === editItem.id ? { ...c, ...editForm } : c));
      setEditItem(null);
    } catch (err) {
      alert(err.message || 'Failed to update test centre');
    } finally {
      setSaving(false);
    }
  };

  const h = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const hEdit = (e) => setEditForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // Edit modal
  const EditModal = editItem ? (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <form onSubmit={handleEditSave} style={{ background:'#fff', borderRadius:12, padding:'24px 20px', width:'100%', maxWidth:520, boxShadow:'0 8px 32px rgba(0,0,0,0.18)', maxHeight:'90vh', overflowY:'auto' }}>
        <h3 style={{ margin:'0 0 18px', fontSize:17, fontWeight:700 }}>Edit Test Centre</h3>
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <label style={{ display:'flex', flexDirection:'column', gap:4, fontSize:13 }}>
            Code
            <input name="code" value={editForm.code} onChange={hEdit} required
              style={{ border:'1px solid #ccc', borderRadius:6, padding:'8px 10px', fontSize:13, width:'100%', boxSizing:'border-box' }} />
          </label>
          <label style={{ display:'flex', flexDirection:'column', gap:4, fontSize:13 }}>
            Centre Name
            <input name="name" value={editForm.name} onChange={hEdit} required
              style={{ border:'1px solid #ccc', borderRadius:6, padding:'8px 10px', fontSize:13, width:'100%', boxSizing:'border-box' }} />
          </label>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <label style={{ display:'flex', flexDirection:'column', gap:4, fontSize:13 }}>
              City
              <input name="city" value={editForm.city} onChange={hEdit} required
                style={{ border:'1px solid #ccc', borderRadius:6, padding:'8px 10px', fontSize:13, width:'100%', boxSizing:'border-box' }} />
            </label>
            <label style={{ display:'flex', flexDirection:'column', gap:4, fontSize:13 }}>
              Postcode
              <input name="postcode" value={editForm.postcode} onChange={hEdit} required
                style={{ border:'1px solid #ccc', borderRadius:6, padding:'8px 10px', fontSize:13, width:'100%', boxSizing:'border-box' }} />
            </label>
          </div>
          <label style={{ display:'flex', flexDirection:'column', gap:4, fontSize:13 }}>
            Region (optional)
            <input name="region" value={editForm.region} onChange={hEdit}
              style={{ border:'1px solid #ccc', borderRadius:6, padding:'8px 10px', fontSize:13, width:'100%', boxSizing:'border-box' }} />
          </label>
        </div>
        <p style={{ margin:'12px 0 4px', fontSize:12, color:'#64748b' }}>Tip: Use format "Area Driving Test Centre" e.g. "Barking Driving Test Centre"</p>
        <div style={{ marginTop:18, display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button type="button" onClick={() => setEditItem(null)}
            style={{ background:'#f1f3f9', border:'1px solid #dde', borderRadius:6, padding:'8px 18px', cursor:'pointer', fontWeight:600, fontSize:13 }}>Cancel</button>
          <button type="submit" disabled={saving}
            style={{ background:'#1043A0', color:'#fff', border:'none', borderRadius:6, padding:'8px 20px', cursor:'pointer', fontWeight:600, fontSize:13 }}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </form>
    </div>
  ) : null;

  return (
    <AdminLayout pageTitle="Test Centres">
    {EditModal}
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px', fontFamily: 'sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>🏫 Test Centres</h1>
        <button
          onClick={() => setShowAdd(s => !s)}
          style={{ marginLeft: 'auto', background: '#1043A0', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontWeight: 600 }}
        >{showAdd ? 'Cancel' : '+ Add Centre'}</button>
      </div>

      {/* Add form */}
      {showAdd && (
        <form onSubmit={handleAdd} style={{ background: '#f8f9ff', border: '1px solid #dde3f4', borderRadius: 10, padding: 20, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>New Test Centre</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              Code (e.g. BAR)
              <input name="code" value={form.code} onChange={h} required
                style={{ border: '1px solid #ccc', borderRadius: 6, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              Centre Name
              <input name="name" value={form.name} onChange={h} required
                style={{ border: '1px solid #ccc', borderRadius: 6, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                City
                <input name="city" value={form.city} onChange={h} required
                  style={{ border: '1px solid #ccc', borderRadius: 6, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                Postcode
                <input name="postcode" value={form.postcode} onChange={h} required
                  style={{ border: '1px solid #ccc', borderRadius: 6, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
              </label>
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              Region (optional)
              <input name="region" value={form.region} onChange={h}
                style={{ border: '1px solid #ccc', borderRadius: 6, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
            </label>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
            <button
              type="submit" disabled={saving}
              style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', cursor: 'pointer', fontWeight: 600 }}
            >{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      )}

      {error && <div style={{ background: '#fff0f0', border: '1px solid #fca', borderRadius: 8, padding: 14, marginBottom: 16, color: '#b00' }}>{error}</div>}

      {/* Search */}
      {!loading && centres.length > 0 && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search by name, city, postcode or region…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: '1 1 260px', border: '1px solid #ccd', borderRadius: 8, padding: '9px 14px', fontSize: 14, outline: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ background: '#f1f3f9', border: '1px solid #dde', borderRadius: 8, padding: '9px 14px', cursor: 'pointer', fontSize: 13, color: '#555', fontWeight: 600 }}
            >✕ Clear</button>
          )}
        </div>
      )}

      {loading ? (
        <p style={{ color: '#666' }}>Loading…</p>
      ) : centres.length === 0 ? (
        <p style={{ color: '#666' }}>No test centres found. Add one above.</p>
      ) : (() => {
        const q = search.trim().toLowerCase();
        const filtered = q
          ? centres.filter(c =>
              (c.name     || '').toLowerCase().includes(q) ||
              (c.city     || '').toLowerCase().includes(q) ||
              (c.postcode || '').toLowerCase().includes(q) ||
              (c.region   || '').toLowerCase().includes(q) ||
              (c.code     || '').toLowerCase().includes(q)
            )
          : centres;
        return filtered.length === 0 ? (
          <p style={{ color: '#888', fontStyle: 'italic' }}>No centres match "{search}".</p>
        ) : (
        <>
        <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f1f3f9', textAlign: 'left' }}>
              {['Code','Name','City','Postcode','Region','Status','Actions'].map(h => (
                <th key={h} style={{ padding: '10px 12px', fontWeight: 600, borderBottom: '2px solid #dde', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c, i) => (
              <tr key={c.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9ff', borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1043A0' }}>{c.code}</td>
                <td style={{ padding: '10px 12px' }}>{c.name}</td>
                <td style={{ padding: '10px 12px' }}>{c.city}</td>
                <td style={{ padding: '10px 12px' }}>{c.postcode}</td>
                <td style={{ padding: '10px 12px', color: '#888' }}>{c.region || '—'}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{
                    display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    background: c.is_active ? '#dcfce7' : '#fee2e2',
                    color: c.is_active ? '#16a34a' : '#dc2626'
                  }}>{c.is_active ? 'Active' : 'Inactive'}</span>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ display:'flex', gap:6 }}>
                  <button
                    onClick={() => openEdit(c)}
                    style={{
                      background: '#eef2ff', color: '#3730a3',
                      border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600
                    }}
                  >Edit</button>
                  <button
                    onClick={() => toggle(c.id, c.is_active)}
                    style={{
                      background: c.is_active ? '#fee2e2' : '#dcfce7',
                      color: c.is_active ? '#dc2626' : '#16a34a',
                      border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600
                    }}
                  >{c.is_active ? 'Deactivate' : 'Activate'}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <p style={{ color: '#888', fontSize: 12, marginTop: 10 }}>
          {q ? `${filtered.length} of ${centres.length}` : centres.length} test centre{centres.length !== 1 ? 's' : ''}
          {q ? ` matching "${search}"` : ''}
        </p>
        </>
        );
      })()}
    </div>
    </AdminLayout>
  );
}
